import {
  EMBED_MESSAGE_SOURCE_HOST,
  EMBED_MESSAGE_SOURCE_IFRAME,
  EMBED_PROTOCOL_VERSION,
} from '../../../lib/embed-contracts/constants'
import { createEmptyCallbacks } from '../../../lib/embed-contracts/callbacks'
import type {
  SDKChatCallback,
  SDKHostCallbacks,
  SDKLoginParams,
  SDKPublicAPI,
  SDKResultCallback,
} from '../../../lib/embed-contracts/public-types'
import type { EmbedRouteId, SDKConfig, SDKResizeOptions } from '../../../lib/embed-contracts/sdk-config'
import type { HostToIframeEvent, IframeCallbackPayload, IframeToHostEvent } from '../../../lib/embed-contracts/protocol'
import { normalizeSDKConfig } from '../config/normalize-config'
import { IframeManager } from '../iframe/iframe-manager'
import { MessageBus } from '../messaging/bus'
import { HandshakeState } from '../messaging/handshake'
import { MessageQueue } from '../messaging/queue'

const SDK_SCRIPT_PATH_SUFFIXES = [
  '/sdk/jssdk',
  '/sdk/ecmas-jssdk.min.js',
]
const DIRECT_TOKEN_STORAGE_KEY = 'ecmas-token'
const LOGIN_USER_STORAGE_KEY = 'ecmas-login-user'
const LOGOUT_SUCCESS_CALLBACK_DATA = {
  code: 0,
  msg: 'ok',
} satisfies Record<string, unknown>

function trimTrailingSlash(value: string) {
  return value.replace(/\/+$/, '')
}

function resolveSDKScriptUrl() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null
  }

  const currentScript = document.currentScript
  if (currentScript instanceof HTMLScriptElement && currentScript.src) {
    return new URL(currentScript.src, window.location.href)
  }

  const scripts = Array.from(document.getElementsByTagName('script'))
  for (let index = scripts.length - 1; index >= 0; index -= 1) {
    const script = scripts[index]
    if (!script.src) {
      continue
    }

    const scriptUrl = new URL(script.src, window.location.href)
    if (SDK_SCRIPT_PATH_SUFFIXES.some((suffix) => scriptUrl.pathname.endsWith(suffix))) {
      return scriptUrl
    }
  }

  return null
}

function resolveImplicitMountConfig(): SDKConfig {
  if (typeof window === 'undefined') {
    return {
      route: 'dashboard',
      mountMode: 'floating',
      autoShow: false,
    }
  }

  const scriptUrl = resolveSDKScriptUrl()
  const matchedSuffix = scriptUrl
    ? SDK_SCRIPT_PATH_SUFFIXES.find((suffix) => scriptUrl.pathname.endsWith(suffix))
    : undefined
  const pathPrefix = matchedSuffix
    ? trimTrailingSlash(scriptUrl?.pathname.slice(0, -matchedSuffix.length) ?? '')
    : ''

  return {
    baseUrl: scriptUrl?.origin ?? window.location.origin,
    pathPrefix,
    route: 'dashboard',
    mountMode: 'floating',
    autoShow: false,
  }
}

export class SDKInstance implements SDKPublicAPI {
  private readonly iframeManager = new IframeManager((visible) => this.notifyIframeVisibility(visible))
  private readonly handshake = new HandshakeState()
  private readonly queue = new MessageQueue()
  private callbacks: SDKHostCallbacks = createEmptyCallbacks()
  private bus = new MessageBus(() => this.iframeManager.getIframe()?.contentWindow ?? null)
  private detachMessageListener: (() => void) | null = null
  private loginCallback: SDKResultCallback | null = null
  private logoutCallback: SDKResultCallback | null = null
  private chatCallback: SDKChatCallback | null = null

  constructor() {
    this.bootstrapFromCachedToken()
  }

  private attachMessageListener() {
    this.detachMessageListener?.()

    const handleMessage = (event: MessageEvent<IframeToHostEvent>) => {
      const message = event.data
      if (!message || typeof message !== 'object') {
        return
      }

      if (message.version !== EMBED_PROTOCOL_VERSION || message.source !== EMBED_MESSAGE_SOURCE_IFRAME) {
        return
      }

      if (event.source !== this.iframeManager.getIframe()?.contentWindow) {
        return
      }

      switch (message.type) {
        case 'ECMAS_IFRAME_READY':
          this.handshake.markReady()
          this.queue.flush((queuedMessage) => this.bus.post(queuedMessage))
          this.callbacks.onReady?.()
          break
        case 'ECMAS_IFRAME_NAVIGATED':
          this.callbacks.onNavigate?.(message.payload.route)
          break
        case 'ECMAS_IFRAME_RESIZE_REQUEST':
          this.iframeManager.resize(message.payload)
          break
        case 'ECMAS_IFRAME_CALLBACK':
          this.handleIframeCallback(message.payload)
          break
        default:
          break
      }
    }

    window.addEventListener('message', handleMessage as EventListener)
    this.detachMessageListener = () => {
      window.removeEventListener('message', handleMessage as EventListener)
    }
  }

  private sendOrQueue(message: HostToIframeEvent) {
    if (this.handshake.isReady()) {
      this.bus.post(message)
      return
    }

    this.queue.enqueue(message)
  }

  private notifyIframeVisibility(visible: boolean) {
    this.sendOrQueue({
      version: EMBED_PROTOCOL_VERSION,
      source: EMBED_MESSAGE_SOURCE_HOST,
      type: visible ? 'ECMAS_SDK_SHOW' : 'ECMAS_SDK_HIDE',
      payload: {},
    })
  }

  private parseStoredJSON(rawValue: string | null) {
    if (!rawValue) {
      return null
    }

    try {
      return JSON.parse(rawValue) as Record<string, unknown>
    } catch {
      return null
    }
  }

  private getStoredDirectToken() {
    if (typeof window === 'undefined') {
      return ''
    }

    return window.localStorage.getItem(DIRECT_TOKEN_STORAGE_KEY)?.trim() ?? ''
  }

  private setStoredDirectToken(token: string) {
    if (typeof window === 'undefined') {
      return
    }

    const trimmed = token.trim()
    if (!trimmed) {
      window.localStorage.removeItem(DIRECT_TOKEN_STORAGE_KEY)
      return
    }

    window.localStorage.setItem(DIRECT_TOKEN_STORAGE_KEY, trimmed)
  }

  private setStoredLoginUser(userInfo: Record<string, unknown>) {
    if (typeof window === 'undefined') {
      return
    }

    if (!userInfo || typeof userInfo !== 'object' || Array.isArray(userInfo)) {
      return
    }

    window.localStorage.setItem(LOGIN_USER_STORAGE_KEY, JSON.stringify(userInfo))
  }

  private clearStoredDirectToken() {
    if (typeof window === 'undefined') {
      return
    }

    window.localStorage.removeItem(DIRECT_TOKEN_STORAGE_KEY)
    window.localStorage.removeItem(LOGIN_USER_STORAGE_KEY)
  }

  private getStoredAccessToken() {
    if (typeof window === 'undefined') {
      return ''
    }

    const currentScene = this.parseStoredJSON(window.localStorage.getItem('__ems_token__'))
    if (typeof currentScene?.access_token === 'string' && currentScene.access_token.trim()) {
      return currentScene.access_token.trim()
    }

    const currentUser = this.parseStoredJSON(window.localStorage.getItem('__ems_user__'))
    if (typeof currentUser?.access_token === 'string' && currentUser.access_token.trim()) {
      return currentUser.access_token.trim()
    }

    const tenantUsers = Array.isArray(currentUser?.tenant_user_vos) ? currentUser.tenant_user_vos : []
    const tenantUser = tenantUsers.find((item) => (
      item
      && typeof item === 'object'
      && typeof (item as Record<string, unknown>).access_token === 'string'
      && (item as Record<string, string>).access_token.trim()
    )) as Record<string, string> | undefined

    return tenantUser?.access_token?.trim() ?? ''
  }

  private resolveStoredLoginPayload() {
    const directToken = this.getStoredDirectToken()
    if (directToken) {
      return {
        ecmasToken: directToken,
      }
    }

    const accessToken = this.getStoredAccessToken()
    if (!accessToken) {
      return null
    }

    return {
      access_token: accessToken,
    }
  }

  private readLoginResultToken(data: Record<string, unknown>) {
    const directToken = data.token
    if (typeof directToken === 'string' && directToken.trim()) {
      return directToken.trim()
    }

    const nestedData = data.data
    if (!nestedData || typeof nestedData !== 'object') {
      return ''
    }

    const nestedToken = (nestedData as Record<string, unknown>).token
    if (typeof nestedToken === 'string' && nestedToken.trim()) {
      return nestedToken.trim()
    }

    const tokenInfo = (nestedData as Record<string, unknown>).tokenInfo
      ?? (nestedData as Record<string, unknown>).token_info
    if (tokenInfo && typeof tokenInfo === 'object' && !Array.isArray(tokenInfo)) {
      const tokenInfoRecord = tokenInfo as Record<string, unknown>
      const tokenValue = tokenInfoRecord.tokenValue ?? tokenInfoRecord.token_value ?? tokenInfoRecord.token
      if (typeof tokenValue === 'string' && tokenValue.trim()) {
        return tokenValue.trim()
      }
    }

    return ''
  }

  private readLoginResultUser(data: Record<string, unknown>) {
    const nestedData = data.data
    if (nestedData && typeof nestedData === 'object' && !Array.isArray(nestedData)) {
      return nestedData as Record<string, unknown>
    }

    return data
  }

  private isSuccessfulLoginResult(data: Record<string, unknown>) {
    return data.code === 200
  }

  private bootstrapFromCachedToken() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }

    if (!this.getStoredDirectToken()) {
      return
    }

    const mountFromCache = () => {
      if (this.iframeManager.getIframe()) {
        return
      }

      this.mountInternal(resolveImplicitMountConfig())
    }

    if (document.body) {
      mountFromCache()
      return
    }

    window.addEventListener('DOMContentLoaded', mountFromCache, { once: true })
  }

  private hasLoginPayload(data: Record<string, unknown>) {
    return [
      data.access_token,
      data.accessToken,
      data.ecmasToken,
      data.token,
      data.userToken,
    ].some((value) => typeof value === 'string' && value.trim())
  }

  private restoreLoginFromHostStorage(extData: Record<string, unknown>) {
    if (this.hasLoginPayload(extData)) {
      return
    }

    const payload = this.resolveStoredLoginPayload()
    if (!payload) {
      return
    }

    this.sendOrQueue({
      version: EMBED_PROTOCOL_VERSION,
      source: EMBED_MESSAGE_SOURCE_HOST,
      type: 'ECMAS_SDK_LOGIN',
      payload,
    })
  }

  private normalizeLoginPayload(params?: SDKLoginParams): Record<string, unknown> {
    if (!params) {
      return {}
    }

    if (typeof params === 'string') {
      const trimmed = params.trim()
      if (!trimmed) {
        return {}
      }

      try {
        const parsed = JSON.parse(trimmed) as Record<string, unknown>
        if (parsed && typeof parsed === 'object') {
          return parsed
        }
      } catch {
        return {
          raw: trimmed,
          token: trimmed,
        }
      }
    }

    return params as Record<string, unknown>
  }

  private resolveLoginPayload(params?: SDKLoginParams) {
    const payload = this.normalizeLoginPayload(params)
    const directToken = this.getStoredDirectToken()
    if (directToken) {
      return {
        ...payload,
        ecmasToken: directToken,
      }
    }

    if (this.hasLoginPayload(payload)) {
      return payload
    }

    const storedPayload = this.resolveStoredLoginPayload()
    if (!storedPayload) {
      return null
    }

    return {
      ...payload,
      ...storedPayload,
    }
  }

  private getCallbackData(data?: Record<string, unknown>) {
    return data ?? {}
  }

  private isAuthRequiredCallback(data?: Record<string, unknown>) {
    return data?.code === 401
  }

  private clearHostLogoutState() {
    this.clearStoredDirectToken()
    this.iframeManager.setLauncherVisible(false)
    this.iframeManager.hide()
  }

  private emitLocalLogoutCallback() {
    this.logoutCallback?.(LOGOUT_SUCCESS_CALLBACK_DATA)
    this.callbacks.onCallback?.('logoutCallback', LOGOUT_SUCCESS_CALLBACK_DATA)
  }

  private emitLegacyChatCallback(type: string, data?: Record<string, unknown>) {
    this.chatCallback?.({
      type,
      msg: data?.msg ?? data,
    })
  }

  private handleIframeCallback(payload: IframeCallbackPayload) {
    const callbackData = this.getCallbackData(payload.data)

    if (this.isAuthRequiredCallback(callbackData)) {
      this.clearStoredDirectToken()
    }

    if (payload.name === 'notifyIsLogin') {
      const isLogin = Boolean(payload.data?.isLogin)
      this.iframeManager.setLauncherVisible(isLogin)

      if (!isLogin) {
        this.iframeManager.hide()
      }
    }

    if (payload.name === 'loginCallback') {
      if (this.isSuccessfulLoginResult(callbackData)) {
        this.setStoredDirectToken(this.readLoginResultToken(callbackData))
        this.setStoredLoginUser(this.readLoginResultUser(callbackData))
      }

      this.loginCallback?.(callbackData)
    }

    if (payload.name === 'logoutCallback') {
      this.clearHostLogoutState()
      this.logoutCallback?.(this.getCallbackData(payload.data))
    }

    if (payload.name === 'requestHide') {
      this.iframeManager.hide()
    }

    if (payload.name === 'gotoLogin') {
      this.clearStoredDirectToken()
      this.iframeManager.setLauncherVisible(false)
      this.iframeManager.hide()
      this.emitLegacyChatCallback('gotoLogin', payload.data)
    }

    if (payload.name === 'callFunction') {
      this.emitLegacyChatCallback('callFunction', payload.data)
    }

    this.callbacks.onCallback?.(payload.name, payload.data)
  }

  private mountInternal(config: SDKConfig = {}, options?: { restoreStoredLogin?: boolean }) {
    const normalized = normalizeSDKConfig(config)
    this.handshake.reset()
    this.queue.clear()
    this.attachMessageListener()
    this.iframeManager.mount(normalized)
    this.sendOrQueue({
      version: EMBED_PROTOCOL_VERSION,
      source: EMBED_MESSAGE_SOURCE_HOST,
      type: 'ECMAS_SDK_INIT',
      payload: {
        route: normalized.route,
        extData: normalized.extData,
      },
    })

    if (options?.restoreStoredLogin ?? true) {
      this.restoreLoginFromHostStorage(normalized.extData)
    }
  }

  private ensureMountedForLogin(payload: Record<string, unknown>) {
    if (this.iframeManager.getIframe()) {
      return
    }

    this.mountInternal({
      ...resolveImplicitMountConfig(),
      extData: payload,
    }, {
      restoreStoredLogin: false,
    })
  }

  mount(config: SDKConfig = {}) {
    this.mountInternal(config)
  }

  show() {
    this.iframeManager.show()
  }

  hide() {
    this.iframeManager.hide()
  }

  toggle() {
    this.iframeManager.toggle()
  }

  resize(options: SDKResizeOptions) {
    this.iframeManager.resize(options)
  }

  navigate(route: EmbedRouteId) {
    this.sendOrQueue({
      version: EMBED_PROTOCOL_VERSION,
      source: EMBED_MESSAGE_SOURCE_HOST,
      type: 'ECMAS_SDK_NAVIGATE',
      payload: { route },
    })
  }

  setExtData(data: Record<string, unknown>) {
    this.sendOrQueue({
      version: EMBED_PROTOCOL_VERSION,
      source: EMBED_MESSAGE_SOURCE_HOST,
      type: 'ECMAS_SDK_SET_EXT_DATA',
      payload: data,
    })
  }

  login(params?: SDKLoginParams, callback?: SDKResultCallback) {
    if (callback) {
      this.loginCallback = callback
    }

    const payload = this.resolveLoginPayload(params)
    if (!payload) {
      return
    }

    this.ensureMountedForLogin(payload)

    this.sendOrQueue({
      version: EMBED_PROTOCOL_VERSION,
      source: EMBED_MESSAGE_SOURCE_HOST,
      type: 'ECMAS_SDK_LOGIN',
      payload,
    })
  }

  logout(callback?: SDKResultCallback) {
    if (callback) {
      this.logoutCallback = callback
    }

    this.clearHostLogoutState()

    if (!this.iframeManager.getIframe()) {
      this.emitLocalLogoutCallback()
      return
    }

    this.sendOrQueue({
      version: EMBED_PROTOCOL_VERSION,
      source: EMBED_MESSAGE_SOURCE_HOST,
      type: 'ECMAS_SDK_LOGOUT',
      payload: {},
    })
  }

  onCallback(callbacks: SDKHostCallbacks) {
    this.callbacks = callbacks
  }

  onChatCallback(callback: SDKChatCallback) {
    this.chatCallback = callback
  }

  destroy() {
    this.detachMessageListener?.()
    this.detachMessageListener = null
    this.handshake.reset()
    this.queue.clear()
    this.callbacks = createEmptyCallbacks()
    this.loginCallback = null
    this.logoutCallback = null
    this.chatCallback = null
    this.iframeManager.destroy()
  }
}
